import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Calendar, Sparkles, ArrowRight, MessageCircle, ChevronRight, Clock, CheckCircle2, X } from 'lucide-react';
import { BOOKING_URL, WHATSAPP_URL, SOCIAL_LINKS } from '../data/constants';
import { useLanguage } from '../context/LanguageContext';
import { AnimatedSection } from './shared';
import { Button } from './ui/button';
import { trackBooking, trackWhatsApp, trackContact } from '../hooks/useAnalytics';

export const HomeContactSection = () => {
  const { language } = useLanguage();
  const isFr = language === 'fr';
  const [formData, setFormData] = useState({ firstName: '', email: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, lastName: '', subject: 'homepage-contact' })
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', email: '', phone: '', message: '' });
      } else setSubmitStatus('error');
    } catch { setSubmitStatus('error'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <section id="home-contact" className="relative py-24 overflow-hidden" data-testid="home-contact-section">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-black to-black" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute top-20 left-[10%] w-[300px] h-[300px] bg-primary/6 rounded-full blur-[120px]" />
      <div className="absolute bottom-20 right-[10%] w-[250px] h-[250px] bg-pink-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <AnimatedSection>
          <div className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/25 rounded-full text-primary text-sm font-medium mb-5">
              <MessageCircle className="w-3.5 h-3.5" />
              {isFr ? 'Contactez-nous' : 'Contact Us'}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {isFr ? 'Une question ?' : 'Have a question?'}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">
                {isFr ? 'Écrivez-nous' : 'Write to us'}
              </span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-lg mx-auto text-base">
              {isFr ? 'Réponse rapide. Remplissez le formulaire ou contactez-nous directement.' : 'Quick response. Fill the form or contact us directly.'}
            </p>
          </div>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Form */}
          <AnimatedSection>
            <form onSubmit={handleSubmit} className="relative rounded-2xl bg-white/[0.04] border border-white/10 p-6 sm:p-8 backdrop-blur-sm">
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-xs font-medium mb-2">{isFr ? 'Prénom' : 'First Name'} *</label>
                    <input type="text" value={formData.firstName} onChange={e => setFormData({...formData, firstName: e.target.value})}
                      className="w-full px-4 py-3.5 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50 focus:bg-white/[0.08] transition-all duration-300"
                      placeholder={isFr ? 'Votre prénom' : 'Your name'} required data-testid="home-contact-name" />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs font-medium mb-2">Email *</label>
                    <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3.5 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50 focus:bg-white/[0.08] transition-all duration-300"
                      placeholder="votre@email.com" required data-testid="home-contact-email" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs font-medium mb-2">{isFr ? 'Téléphone' : 'Phone'}</label>
                  <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3.5 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50 focus:bg-white/[0.08] transition-all duration-300"
                    placeholder="+41 XX XXX XX XX" data-testid="home-contact-phone" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs font-medium mb-2">Message *</label>
                  <textarea value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} rows={4}
                    className="w-full px-4 py-3.5 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-primary/50 focus:bg-white/[0.08] resize-none transition-all duration-300"
                    placeholder={isFr ? 'Vos objectifs, questions, disponibilités...' : 'Your goals, questions, availability...'} required data-testid="home-contact-message" />
                </div>

                {submitStatus === 'success' && (
                  <div className="p-3.5 bg-green-500/15 border border-green-500/30 rounded-xl text-green-400 text-sm flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 shrink-0" /> {isFr ? 'Message envoyé avec succès !' : 'Message sent successfully!'}
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="p-3.5 bg-red-500/15 border border-red-500/30 rounded-xl text-red-400 text-sm flex items-center gap-2">
                    <X className="w-4 h-4 shrink-0" /> {isFr ? 'Erreur. Réessayez ou appelez-nous directement.' : 'Error. Try again or call us directly.'}
                  </div>
                )}

                <Button type="submit" disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-white py-6 text-base font-semibold rounded-xl hover:scale-[1.01] transition-all duration-300 shadow-lg shadow-primary/15 disabled:opacity-50"
                  data-testid="home-contact-submit">
                  {isSubmitting ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />{isFr ? 'Envoi en cours...' : 'Sending...'}</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-2" />{isFr ? 'Envoyer le message' : 'Send message'}</>
                  )}
                </Button>
              </div>
            </form>
          </AnimatedSection>

          {/* Quick contact cards */}
          <AnimatedSection delay={200}>
            <div className="space-y-4">
              <a href="tel:+41786044423" className="group flex items-center gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary/30 hover:bg-white/[0.06] transition-all duration-300" data-testid="home-contact-phone-link">
                <div className="w-12 h-12 bg-primary/12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-gray-500 text-xs">{isFr ? 'Téléphone' : 'Phone'}</div>
                  <div className="text-white text-base font-semibold">+41 78 604 44 23</div>
                </div>
              </a>

              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 p-5 rounded-2xl bg-green-500/[0.05] border border-green-500/15 hover:border-green-500/35 hover:bg-green-500/[0.08] transition-all duration-300" data-testid="home-contact-whatsapp-link">
                <div className="w-12 h-12 bg-green-500/15 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <MessageCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <div className="text-gray-500 text-xs">WhatsApp</div>
                  <div className="text-green-400 text-base font-semibold">{isFr ? 'Écrivez-nous !' : 'Message us!'}</div>
                </div>
              </a>

              <a href="mailto:info@body-up.ch" className="group flex items-center gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary/30 hover:bg-white/[0.06] transition-all duration-300" data-testid="home-contact-email-link">
                <div className="w-12 h-12 bg-primary/12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-gray-500 text-xs">Email</div>
                  <div className="text-white text-base font-semibold">info@body-up.ch</div>
                </div>
              </a>

              <Link to="/horaires" className="group flex items-center gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-primary/30 hover:bg-white/[0.06] transition-all duration-300" data-testid="home-contact-horaires-link">
                <div className="w-12 h-12 bg-primary/12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-gray-500 text-xs">{isFr ? 'Horaires d\'ouverture' : 'Opening Hours'}</div>
                  <div className="text-white text-base font-semibold">{isFr ? 'Lun-Ven 6h30–20h · Sam 8h30–14h30' : 'Mon-Fri 6:30AM–8PM · Sat 8:30AM–2:30PM'}</div>
                </div>
              </Link>

              {/* CTA Booking */}
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking('contact_section')} className="group flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-primary/10 to-pink-500/5 border border-primary/25 hover:border-primary/50 transition-all duration-300" data-testid="home-contact-book-link">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-gray-500 text-xs">{isFr ? 'Prêt à commencer ?' : 'Ready to start?'}</div>
                  <div className="text-primary text-base font-bold">{isFr ? 'Réserver une séance' : 'Book a session'}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-primary/50 ml-auto group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Home Page
